<script lang="ts" setup>
import { useReferentialStore } from '~/stores/referential';
import { useTranslations } from '~/composables/translations';
import { computed, ref } from 'vue';

import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';
import vuAccordion from '@ds/vuekit/src/components/vu-accordion.vue';
import Image from '~/components/common/Image.vue';

import { ProcessFamily } from '../../models/referential';
import { NlsProcessFamilies } from '~/typings/nls/process_families';

// VARIABLES
const accordionModels = ref(Array(20).fill([]));
const imgBaseUrl = window.dsDefaultWebappsBaseUrl;

// PROPS
interface Props {
  processFamilies?: any[];
  printers: any[];
  isCardView?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  processFamilies: undefined,
  printers: undefined,
  isCardView: false,
});

// EMITS
const emit = defineEmits(['toggle']);

// NLS
const { $i18n } = useTranslations('pages/lab/showroom');
const { $i18n: $i18nMachines } = useTranslations('components/showroom/machines');
const { $i18n: $i18nProcessF } = useTranslations('process_families');

// STORES
const referential = useReferentialStore();

// COMPUTED
const allTabsOpen = computed(
  () => Object.values(printersByProcessFamilyId).length === accordionModels.value.length,
);
const processFamiliesMap = computed(
  () => referential.mapProcessFamilies as Record<string, ProcessFamily>,
);

const printersByProcessFamilyId = computed(() =>
  props.printers.reduce((acc, el) => {
    if (!acc[el.processFamilyId]) {
      acc[el.processFamilyId] = [];
    }

    acc[el.processFamilyId].push(el);

    return acc;
  }, {}),
);

const materialsByPrinterId = computed(() =>
  props.printers.reduce((acc, el) => {
    if (!acc[el.printerId]) {
      acc[el.printerId] = [];
    }

    acc[el.printerId] = el.offers

      .map(
        (o: { materialFamilyId: string | number }) =>
          referential.mapMaterials.family[o.materialFamilyId],
      )
      .filter((m: any) => m);

    return acc;
  }, {}),
);

const uniqueMaterialsByPrinterId = computed(() =>
  props.printers.reduce((acc, el) => {
    if (!acc[el.printerId]) {
      acc[el.printerId] = [];
    }

    acc[el.printerId] = [...new Set(materialsByPrinterId.value[el.printerId])].join(', ');
    return acc;
  }, {}),
);

// METHODS
const openAllAccordions = (open: any) => {
  accordionModels.value = [];
  if (open) {
    for (let i = 1; i < Object.values(printersByProcessFamilyId).length + 1; i += 1) {
      accordionModels.value.push(i);
    }

    emit('toggle');
  }
};
</script>
<template>
  <div class="showroom-machines-container pb-10px">
    <div class="flex align-items-center">
      <div class="flex-grow-1 showroom-section-subtitle text--uppercase">
        {{ $i18n('machinesTitle') }}
      </div>
      <div v-if="props.printers.length" class="expand-bloc mb-5px flex align-items-center">
        <vu-icon
          :class="['chevron', accordionModels.length ? 'expanded' : null]"
          icon="chevron-down"
        />
        <div
          class="expand-all mp3dp-link"
          style="align-self: self-end"
          @click="openAllAccordions(!allTabsOpen)"
        >
          {{ allTabsOpen ? $i18n('collapseAll') : $i18n('expandAll') }}
        </div>
      </div>
    </div>
    <div class="showroom-container">
      <vu-accordion
        v-if="props.printers.length"
        :animated="true"
        v-model="accordionModels"
        :items="Object.values(printersByProcessFamilyId).length"
      >
        <template
          v-for="(printersOfFamily, processFamilyId, index) in printersByProcessFamilyId"
          #[`title-${index+1}`]
          :key="index + 'process-family-id'"
        >
          <div
            class="flex flex-wrap printer-by-process"
            style="align-items: flex-end"
            @click="$emit('toggle', printersOfFamily)"
          >
            <div class="process-title" style="padding: 0px">
              {{
                $i18nProcessF(
                  referential.mapProcessFamilies[processFamilyId] &&
                    (referential.mapProcessFamilies[processFamilyId]
                      .name as keyof NlsProcessFamilies),
                )
              }}
            </div>
            <div class="flex">
              <div class="px-5px separator">-</div>
              <div class="printers-count" style="color: gray; font-weight: normal">
                {{ printersOfFamily.length }}
                <template v-if="printersOfFamily.length > 1">
                  {{ $i18n('machinesTitle').toLowerCase() }}
                </template>
                <template v-else>
                  {{ $i18n('machine').toLowerCase() }}
                </template>
              </div>
            </div>
          </div>
        </template>
        <!-- MOBILE CARD VIEW -->
        <template
          v-for="(printersOfFamily, processFamilyId, index) in printersByProcessFamilyId"
          #[`item-${index+1}`]
          :key="index + 'process-family-row'"
        >
          <div v-if="isCardView">
            <template v-for="(printer, indexPrint) in printersOfFamily">
              <div
                v-if="referential.mapMachines[printer.printerId]"
                :key="indexPrint"
                class="elevated flex flex-column mobile my-10px"
              >
                <Image
                  v-if="processFamiliesMap[processFamilyId]"
                  img-class="printer-img align-self-center justify-self-center"
                  :src="
                    'https://s3-eu-west-1.amazonaws.com/3dmarketplace/machines/' +
                    referential.mapMachines[printer.printerId].image
                  "
                  :error-src="
                    imgBaseUrl +
                    'MP3DPCommonViews/assets/graphics/processes_icons/250x190/' +
                    processFamiliesMap[processFamilyId].name +
                    '.png'
                  "
                />
                <div class="grid grid-2-columns">
                  <div class="header-item flex align-items-center model-title pa-10px">
                    {{ $i18nMachines('model') }}
                  </div>
                  <div class="printer-grid-item model">
                    {{ referential.mapMachines[printer.printerId].model }}
                  </div>
                  <div class="header-item flex align-items-center printer-process-title pa-10px">
                    {{ $i18nMachines('processLabel') }}
                  </div>
                  <div class="printer-grid-item process">
                    {{ referential.mapMachines[printer.printerId].process.name }}
                  </div>
                  <div class="header-item flex align-items-center manufacturer-title pa-10px">
                    {{ $i18nMachines('manufacturer') }}
                  </div>
                  <div class="printer-grid-item manufacturer">
                    {{ referential.mapMachines[printer.printerId].manufacturer || '/' }}
                  </div>
                  <div class="header-item flex align-items-center max-bounding-box-title pa-10px">
                    {{ $i18nMachines('maxBBox') }}
                  </div>
                  <div class="printer-grid-item maxBoundingBox">
                    {{
                      `${referential.mapMachines[printer.printerId].maxVolumeBoundaryX}
                      x ${referential.mapMachines[printer.printerId].maxVolumeBoundaryY}
                      x ${referential.mapMachines[printer.printerId].maxVolumeBoundaryZ} mm`
                    }}
                  </div>
                  <div class="header-item flex align-items-center materials-title pa-10px">
                    {{ $i18nMachines('materialLabel') }}
                  </div>
                  <div class="fade-keyframe printer-grid-item materials">
                    <div
                      v-if="!materialsByPrinterId[printer.printerId].length"
                      class="loading-gray-box shimmer w-100 h-20px"
                    />
                    <div v-else class="list">
                      {{ uniqueMaterialsByPrinterId[printer.printerId] }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <!-- DESKTOP ROW VIEW -->
          <div v-else :key="index + 'process-row'" class="printer-grid">
            <div
              v-for="(printer, indexPrint) in printersOfFamily"
              :key="indexPrint + 'printer-template'"
              class="printer display-contents"
            >
              <template v-if="!indexPrint">
                <div class="header-item flex align-items-center machines-title pa-10px pl-30px">
                  {{ $i18n('machinesTitle') }}
                </div>
                <div class="header-item flex align-items-center printer-process-title pa-10px">
                  {{ $i18nMachines('processLabel') }}
                </div>
                <div class="header-item flex align-items-center manufacturer-title pa-10px">
                  {{ $i18nMachines('manufacturer') }}
                </div>
                <div class="header-item flex align-items-center model-title pa-10px">
                  {{ $i18nMachines('model') }}
                </div>
                <div class="header-item flex align-items-center max-bounding-box-title pa-10px">
                  {{ $i18nMachines('maxBBox') }}
                </div>
                <div class="header-item flex align-items-center materials-title pa-10px">
                  {{ $i18nMachines('materialLabel') }}
                </div>
              </template>
              <template v-if="referential.mapMachines[printer.printerId]">
                <Image
                  v-if="processFamiliesMap[processFamilyId]"
                  img-class="printer-img"
                  :src="
                    'https://s3-eu-west-1.amazonaws.com/3dmarketplace/machines/' +
                    referential.mapMachines[printer.printerId].image
                  "
                  :error-src="
                    imgBaseUrl +
                    'MP3DPCommonViews/assets/graphics/processes_icons/250x190/' +
                    processFamiliesMap[processFamilyId].name +
                    '.png'
                  "
                />
                <div class="printer-grid-item process">
                  {{ referential.mapMachines[printer.printerId].process.name }}
                </div>
                <div class="printer-grid-item manufacturer">
                  {{ referential.mapMachines[printer.printerId].manufacturer || '/' }}
                </div>
                <div class="printer-grid-item model">
                  {{ referential.mapMachines[printer.printerId].model }}
                </div>
                <div class="printer-grid-item maxBoundingBox">
                  {{
                    `${referential.mapMachines[printer.printerId].maxVolumeBoundaryX} x
                    ${referential.mapMachines[printer.printerId].maxVolumeBoundaryY}
                    x ${referential.mapMachines[printer.printerId].maxVolumeBoundaryZ} mm`
                  }}
                </div>
                <div class="fade-keyframe printer-grid-item materials">
                  <div
                    v-if="!materialsByPrinterId[printer.printerId].length"
                    class="loading-gray-box shimmer w-100 h-20px"
                  />
                  <div v-else class="list">
                    {{ uniqueMaterialsByPrinterId[printer.printerId] }}
                  </div>
                </div>
                <div v-if="indexPrint < printersOfFamily.length - 1" class="border" />
              </template>
            </div>
          </div>
        </template>
      </vu-accordion>
      <div v-else class="pa-10px no-item">
        {{ $i18n('noOfferYet') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
// Sliding animation for the vu-accordion
.accordion-container {
  &:not(.active) {
    .accordion-item.active .content-wrapper .content {
      animation: slide-from-top 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
    }
  }

  .accordion .accordion-item .accordion-title .caret-left {
    margin-top: 10px;
  }

  .accordion-title {
    &:hover {
      background: var(--grey-1);
    }
  }

  .accordion-item {
    &:not(:first-child) {
      border-top: 1px solid var(--grey-4);
    }
  }
}
</style>

<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;
@forward '/assets/scss/skeletons';

.chevron {
  transition: transform 0.1s ease-in-out;
  transform: rotateZ(0);

  &.expanded {
    transform: rotateZ(-180deg);
    margin-bottom: 2px;
  }
}

.printers-count {
  font-size: 16px;
}

.process-title {
  white-space: nowrap;
  font-size: 16px;
  font-weight: bold;
  color: var(--grey-6);
}

.separator {
  color: var(--grey-5);
  font-size: 14px;
  margin-bottom: 2px;
}

.header-item {
  font-weight: bold;
  background: #ebebeb;
  min-height: 60px;
}

.mobile {
  border: 1px solid var(--grey-4);

  .model {
    color: rgb(54, 142, 196);
  }

  .printer-img {
    max-height: 180px;
    min-height: 150px;
    max-width: 320px;
    border-radius: 0px;
    width: fit-content;
  }

  .mobile-grid {
    border-top: 1px solid var(--grey-4);
  }
}

.printer-img {
  object-fit: cover;
  width: 100%;
  max-width: 160px;
  min-height: 120px;
}

.border {
  grid-column: 1 / -1;
  height: 2px;
  border-bottom: 2px solid #e5e5e5;
  width: 100%;
}

.printer-grid-item {
  padding: 10px;
}

.printer-grid {
  padding: 10px;
  display: grid;
  align-items: center;
  grid-template-columns: repeat(6, 1fr);
  row-gap: 10px;
  overflow-y: auto;
}

.content.accordion-animated-content {
  padding-bottom: 0;
  padding-top: 0;
}
</style>
